import { capitalize } from '@material-ui/core/styles';
import { endOfDay, format, parse, startOfDay } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import caLocale from 'date-fns/locale/ca';
import daLocale from 'date-fns/locale/da';
import deLocale from 'date-fns/locale/de';
import enLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import fiLocale from 'date-fns/locale/fi';
import frLocale from 'date-fns/locale/fr';
import itLocale from 'date-fns/locale/it';
import jaLocale from 'date-fns/locale/ja';
import noLocale from 'date-fns/locale/nb';
import nlLocale from 'date-fns/locale/nl';
import plLocale from 'date-fns/locale/pl';
import ptLocale from 'date-fns/locale/pt';
import ruLocale from 'date-fns/locale/ru';
import svLocale from 'date-fns/locale/sv';
import { isNil } from 'ramda';
import { PaymentProviders, PaymentStatuses, SignupType, UserRoles, ZeroDecimalCurrencies } from './constants';

const localeMap = {
  en: enLocale,
  de: deLocale,
  es: esLocale,
  fr: frLocale,
  nl: nlLocale,
  pl: plLocale,
  pt: ptLocale,
  sv: svLocale,
  ja: jaLocale,
  da: daLocale,
  it: itLocale,
  no: noLocale,
  fi: fiLocale,
  ru: ruLocale,
  ca: caLocale
};

export function getDateFnsLocale(code = 'en') {
  return localeMap[code];
}

export function debounce(targetFunction, timeoutMs) {
  let timeout = null;

  return (...args) => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => targetFunction(...args), timeoutMs);
  };
}

const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export const currencies = {
  eur: 'EUR €',
  usd: 'USD $',
  gbp: 'GBP £',
  aed: 'AED د.إ',
  brl: 'BRL R$',
  chf: 'CHF ₣',
  cad: 'CAD $',
  aud: 'AUD $',
  inr: 'INR ₹',
  cny: 'CNY ¥',
  dkk: 'DKK',
  hkd: 'HK$',
  jpy: 'JPY ¥',
  nzd: 'NZ$',
  sek: 'SEK',
  huf: 'HUF Ft',
  nok: 'NOK',
  ars: 'ARS $'
};

export const currencySymbols = {
  eur: '€',
  usd: '$',
  gbp: '£',
  aed: 'د.إ',
  brl: 'R$',
  chf: '₣',
  cad: '$',
  aud: '$',
  inr: '₹',
  cny: '¥',
  dkk: 'DKK',
  hkd: 'HK$',
  jpy: '¥',
  krw: '₩',
  nzd: 'NZ$',
  sek: 'SEK',
  huf: 'Ft',
  nok: 'NOK',
  ars: '$'
};


export const relevantCurrencies = {
  cad: 'CAD $',
  eur: 'EUR €',
  gbp: 'GBP £',
  usd: 'USD $'
};

// Exhaustive list of countries offered on the signup & profile dropdowns. Per
// card 54 we feature *every* country so anyone can register; whether tipping is
// actually available is a separate concern derived from `tipDirectCountries`
// (see `isTippingSupportedCountry`) and enforced as `userFeatures.tipsDisabled`
// on the backend. Two legacy non-standard codes are preserved so existing user
// records keep matching a dropdown option: Luxembourg = `lux`, Malta = `mlt`.
export const countries = {
  af: 'Afghanistan',
  al: 'Albania',
  dz: 'Algeria',
  ad: 'Andorra',
  ao: 'Angola',
  ag: 'Antigua and Barbuda',
  ar: 'Argentina',
  am: 'Armenia',
  au: 'Australia',
  at: 'Austria',
  az: 'Azerbaijan',
  bs: 'Bahamas',
  bh: 'Bahrain',
  bd: 'Bangladesh',
  bb: 'Barbados',
  by: 'Belarus',
  be: 'Belgium',
  bz: 'Belize',
  bj: 'Benin',
  bt: 'Bhutan',
  bo: 'Bolivia',
  ba: 'Bosnia and Herzegovina',
  bw: 'Botswana',
  br: 'Brazil',
  bn: 'Brunei',
  bg: 'Bulgaria',
  bf: 'Burkina Faso',
  bi: 'Burundi',
  cv: 'Cabo Verde',
  kh: 'Cambodia',
  cm: 'Cameroon',
  ca: 'Canada',
  cf: 'Central African Republic',
  td: 'Chad',
  cl: 'Chile',
  cn: 'China',
  co: 'Colombia',
  km: 'Comoros',
  cg: 'Congo (Republic)',
  cd: 'Congo (DRC)',
  cr: 'Costa Rica',
  ci: "Côte d'Ivoire",
  hr: 'Croatia',
  cu: 'Cuba',
  cy: 'Cyprus',
  cz: 'Czech Republic',
  dk: 'Denmark',
  dj: 'Djibouti',
  dm: 'Dominica',
  do: 'Dominican Republic',
  ec: 'Ecuador',
  eg: 'Egypt',
  sv: 'El Salvador',
  gq: 'Equatorial Guinea',
  er: 'Eritrea',
  ee: 'Estonia',
  sz: 'Eswatini',
  et: 'Ethiopia',
  fj: 'Fiji',
  fi: 'Finland',
  fr: 'France',
  ga: 'Gabon',
  gm: 'Gambia',
  ge: 'Georgia',
  de: 'Germany',
  gh: 'Ghana',
  gi: 'Gibraltar',
  gr: 'Greece',
  gd: 'Grenada',
  gt: 'Guatemala',
  gn: 'Guinea',
  gw: 'Guinea-Bissau',
  gy: 'Guyana',
  ht: 'Haiti',
  hn: 'Honduras',
  hk: 'Hong Kong',
  hu: 'Hungary',
  is: 'Iceland',
  in: 'India',
  id: 'Indonesia',
  ir: 'Iran',
  iq: 'Iraq',
  ie: 'Ireland',
  il: 'Israel',
  it: 'Italy',
  jm: 'Jamaica',
  jp: 'Japan',
  jo: 'Jordan',
  kz: 'Kazakhstan',
  ke: 'Kenya',
  ki: 'Kiribati',
  xk: 'Kosovo',
  kw: 'Kuwait',
  kg: 'Kyrgyzstan',
  la: 'Laos',
  lv: 'Latvia',
  lb: 'Lebanon',
  ls: 'Lesotho',
  lr: 'Liberia',
  ly: 'Libya',
  li: 'Liechtenstein',
  lt: 'Lithuania',
  lux: 'Luxembourg',
  mg: 'Madagascar',
  mw: 'Malawi',
  my: 'Malaysia',
  mv: 'Maldives',
  ml: 'Mali',
  mlt: 'Malta',
  mh: 'Marshall Islands',
  mr: 'Mauritania',
  mu: 'Mauritius',
  mx: 'Mexico',
  fm: 'Micronesia',
  md: 'Moldova',
  mc: 'Monaco',
  mn: 'Mongolia',
  me: 'Montenegro',
  ma: 'Morocco',
  mz: 'Mozambique',
  mm: 'Myanmar',
  na: 'Namibia',
  nr: 'Nauru',
  np: 'Nepal',
  nl: 'Netherlands',
  nz: 'New Zealand',
  ni: 'Nicaragua',
  ne: 'Niger',
  ng: 'Nigeria',
  kp: 'North Korea',
  mk: 'North Macedonia',
  no: 'Norway',
  om: 'Oman',
  pk: 'Pakistan',
  pw: 'Palau',
  ps: 'Palestine',
  pa: 'Panama',
  pg: 'Papua New Guinea',
  py: 'Paraguay',
  pe: 'Peru',
  ph: 'Philippines',
  pl: 'Poland',
  pt: 'Portugal',
  qa: 'Qatar',
  ro: 'Romania',
  ru: 'Russia',
  rw: 'Rwanda',
  kn: 'Saint Kitts and Nevis',
  lc: 'Saint Lucia',
  vc: 'Saint Vincent and the Grenadines',
  ws: 'Samoa',
  sm: 'San Marino',
  st: 'Sao Tome and Principe',
  sa: 'Saudi Arabia',
  sn: 'Senegal',
  rs: 'Serbia',
  sc: 'Seychelles',
  sl: 'Sierra Leone',
  sg: 'Singapore',
  sk: 'Slovakia',
  si: 'Slovenia',
  sb: 'Solomon Islands',
  so: 'Somalia',
  za: 'South Africa',
  kr: 'South Korea',
  ss: 'South Sudan',
  es: 'Spain',
  lk: 'Sri Lanka',
  sd: 'Sudan',
  sr: 'Suriname',
  se: 'Sweden',
  ch: 'Switzerland',
  sy: 'Syria',
  tw: 'Taiwan',
  tj: 'Tajikistan',
  tz: 'Tanzania',
  th: 'Thailand',
  tl: 'Timor-Leste',
  tg: 'Togo',
  to: 'Tonga',
  tt: 'Trinidad and Tobago',
  tn: 'Tunisia',
  tr: 'Turkey',
  tm: 'Turkmenistan',
  tv: 'Tuvalu',
  ug: 'Uganda',
  ua: 'Ukraine',
  ae: 'United Arab Emirates',
  gb: 'United Kingdom',
  us: 'United States',
  uy: 'Uruguay',
  uz: 'Uzbekistan',
  vu: 'Vanuatu',
  va: 'Vatican City',
  ve: 'Venezuela',
  vn: 'Vietnam',
  ye: 'Yemen',
  zm: 'Zambia',
  zw: 'Zimbabwe'
};

// Mirror of the canonical `tippingSupportedCountryCodes` in
// backend/src/util/index.ts — the EU 27 plus UK, US and Canada (Trello
// 8Lpkwp34). EFTA (NO, CH, LI, IS) and AR/ME were removed: we cannot pay guides
// out there. A drift-guard test parses the backend file and fails if these two
// lists disagree, so change the backend first.
export const tipDirectCountries = {
  at: 'Austria',
  be: 'Belgium',
  bg: 'Bulgaria',
  hr: 'Croatia',
  cy: 'Cyprus',
  cz: 'Czech Republic',
  dk: 'Denmark',
  ee: 'Estonia',
  fi: 'Finland',
  fr: 'France',
  de: 'Germany',
  gr: 'Greece',
  hu: 'Hungary',
  ie: 'Ireland',
  it: 'Italy',
  lv: 'Latvia',
  lt: 'Lithuania',
  lu: 'Luxembourg',
  mt: 'Malta',
  nl: 'Netherlands',
  pl: 'Poland',
  pt: 'Portugal',
  ro: 'Romania',
  sk: 'Slovakia',
  si: 'Slovenia',
  es: 'Spain',
  se: 'Sweden',
  gb: 'United Kingdom',
  us: 'United States',
  ca: 'Canada'
};

// Every geographically-European country routes to the EU region; everything
// else routes to US (the historical "rest of world" bucket). This preserves the
// exact mapping the hand-maintained list used for the original supported
// countries, and — by deriving `countryToRegion` from `countries` below —
// guarantees every dropdown country resolves to a region, so signup never sends
// an undefined `region` for a newly-added country.
const europeanCountryCodes = new Set([
  'ad', 'al', 'at', 'ba', 'be', 'bg', 'by', 'ch', 'cy', 'cz', 'de', 'dk', 'ee',
  'es', 'fi', 'fr', 'gb', 'gi', 'gr', 'hr', 'hu', 'ie', 'is', 'it', 'li', 'lt',
  'lux', 'lv', 'mc', 'md', 'me', 'mk', 'mlt', 'nl', 'no', 'pl', 'pt', 'ro', 'rs',
  'ru', 'se', 'si', 'sk', 'sm', 'ua', 'va', 'xk'
]);

export const countryToRegion = Object.fromEntries(
  Object.keys(countries).map(code => [
    code,
    europeanCountryCodes.has(code) ? 'EU' : 'US'
  ])
);

// Codes here mirror the backend `tippingSupportedCountryCodes` (EU 27 +
// GB/US/CA). `tipDirectCountries` stores the ISO `lu`/`mt` spellings while
// the `countries` dropdown stores the legacy `lux`/`mlt`, so accept both. This
// is the single frontend read of "is tipping available in this country?"; the
// backend enforces the same via `userFeatures.tipsDisabled`.
const tippingSupportedCountryCodes = new Set([
  ...Object.keys(tipDirectCountries),
  'lux',
  'mlt'
]);

export const isTippingSupportedCountry = country =>
  Boolean(country) && tippingSupportedCountryCodes.has(country.toLowerCase());

// The tipping-supported subset of `countries`, keyed by the same codes/labels as
// the full list (so `lux`/`mlt` stay consistent — unlike `tipDirectCountries`,
// which uses `lu`/`mt`). Used to restrict the profile country dropdown for users
// already set up for tipping.
export const tippingSupportedCountries = Object.fromEntries(
  Object.entries(countries).filter(([code]) => isTippingSupportedCountry(code))
);

export const supportedLanguages = [
  'en',
  'ca',
  'da',
  'de',
  'es',
  'fi',
  'fr',
  'it',
  'ja',
  'nl',
  'no',
  'pl',
  'pt',
  'ru',
  'sv'
];
export const languageCodesToLanguageNames = {
  en: 'English',
  fr: 'French',
  es: 'Spanish',
  de: 'German',
  no: 'Norwegian',
  pl: 'Polish',
  ru: 'Russian',
  pt: 'Portuguese',
  nl: 'Dutch',
  sv: 'Swedish',
  ja: 'Japanese',
  da: 'Danish',
  it: 'Italian',
  fi: 'Finnish',
  ca: 'Catalan'
};


// Countries whose guides were onboarded onto the US Stripe account. Mirrors
// `crossBorderCountryCodes` in backend/src/util/index.ts: Canada left it in the
// card 445 hotfix and now uses the EU account. Iceland stays (Stripe cannot open
// Icelandic accounts on the EU account) and, like the rest, can't be tipped.
const crossBorderCountryCodes = [
  "IS",
  "AL",
  "DZ",
  "AO",
  "AG",
  "AR",
  "AM",
  "AU",
  "AZ",
  "BS",
  "BH",
  "BD",
  "BJ",
  "BT",
  "BO",
  "BA",
  "BW",
  "BN",
  "KH",
  "CL",
  "CO",
  "CR",
  "CI",
  "DO",
  "EC",
  "EG",
  "SV",
  "ET",
  "GA",
  "GM",
  "GH",
  "GT",
  "GY",
  "HK",
  "IN",
  "ID",
  "IL",
  "JM",
  "JP",
  "JO",
  "KZ",
  "KE",
  "KW",
  "LA",
  "MG",
  "MY",
  "MU",
  "MX",
  "MD",
  "MN",
  "MA",
  "MZ",
  "NA",
  "NZ",
  "NE",
  "NG",
  "OM",
  "PK",
  "PA",
  "PY",
  "PE",
  "PH",
  "QA",
  "RW",
  "SA",
  "SN",
  "RS",
  "SG",
  "ZA",
  "KR",
  "LK",
  "LC",
  "TW",
  "TZ",
  "TH",
  "TT",
  "TN",
  "TR",
  "AE",
  "UY",
  "UZ",
  "VN"
];


export function shouldUseUSStripe(guideCountry) {
  const upperCaseCountry = guideCountry.toUpperCase();
  return crossBorderCountryCodes.includes(upperCaseCountry);
};

export function isStripePaymentsActive() {
  return true;
}

export function userIsCrossBorder(guideCountry) {
  if (!guideCountry) return false;
  return crossBorderCountryCodes.includes(guideCountry.toUpperCase());
}

// Guides who are merchant of record for their own tips (direct charges on
// their own Stripe account, so card_payments as well as transfers, and no team
// tipping). Mirrors `isMerchantOfRecordCountry` in backend/src/util/index.ts;
// Canada joined the US in the card 445 hotfix.
const merchantOfRecordCountryCodes = ['US', 'CA'];

export function isMerchantOfRecordCountry(guideCountry) {
  if (!guideCountry) return false;
  return merchantOfRecordCountryCodes.includes(String(guideCountry).toUpperCase());
}

export function langCodeToName(langCode) {
  return languageCodesToLanguageNames[langCode] ?? capitalize(langCode);
}

const currenciesDecimalCountOverrides = {};
function getCurrencyDecimalPoints(currency) {
  return currenciesDecimalCountOverrides[currency] ?? 2;
}

export function date2day(date) {
  return days[date.getDay()];
}

export function date2str(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(
    -2
  )}`;
}

export function getStartOfDayInZeroTimezone(d) {
  const date = startOfDay(new Date(d));
  return new Date(date.getTime() + getFormattedOffsetTime(date.getTimezoneOffset()));
}

export function seasonByDate(seasons, date) {
  const d = new Date(date);
  return seasons.find(season => new Date(season.start) <= d && endOfDay(new Date(season.end)) > d);
}

export function date2strSlash(date) {
  return format(new Date(date), 'dd/MM/yyyy');
}

// Single source of truth mapping a REGION to the customer-facing TA-Tipping
// origin. `local` points at a local TA-Tipping (default dev port 3002); any
// unknown region falls back to production US.
export const regionBaseUrl = region => {
  switch (region) {
    case 'local':
      return 'http://localhost:3002';
    case 'QA':
      return 'https://qats.tripadmit.com';
    case 'EU':
      return 'https://eu.tip.direct';
    default:
      return 'https://us.tip.direct';
  }
};

export const formatRegionalLinks = path => `${regionBaseUrl(process.env.REGION)}/${path}`;

// Resolve a stored logo/image field to a usable src.
//
// We used to host logos locally, so the field held a relative path/filename and
// the app prepended the current origin. Logos are now uploaded to a CDN
// (Cloudinary) and the field holds an absolute URL, so prepending the origin
// produced broken `http://localhost:3001/http://res.cloudinary.com/...` URLs.
//
// Both shapes are still out in the wild, so: absolute URLs are used as-is (with
// http upgraded to https, since Cloudinary delivery URLs come back as http and
// the browser blocks them as mixed content on our https pages); anything else
// is treated as a legacy relative path and gets the current origin prepended.
export const getCompanyLogoUrl = logo => {
  // Defensive: the field has historically held inconsistent shapes (a bare
  // string, or a one-element array straight off an upload response). Anything
  // that isn't a usable string degrades to no image rather than crashing render.
  if (!logo || typeof logo !== 'string') return '';
  if (logo.startsWith('http')) return logo.replace(/^http:/, 'https:');
  return `${window.location.protocol}//${window.location.host}/${
    logo.startsWith('/') ? logo.substring(1) : logo
  }`;
};

// Origin (protocol + host) of the customer-facing TA-Tipping app. Used as the
// live-preview iframe origin and the postMessage targetOrigin.
//
// TIPPING_PREVIEW_ORIGIN is a preview-only override: the region .env files bake
// REGION=US for dev, so an explicit override lets you point the preview at your
// local TA-Tipping without touching REGION (which gates PrivateRoute
// rendering). Absent the override it resolves purely from REGION.
export const getTippingOrigin = () => {
  if (process.env.TIPPING_PREVIEW_ORIGIN) {
    return process.env.TIPPING_PREVIEW_ORIGIN;
  }
  return regionBaseUrl(process.env.REGION);
};

export function parseSeasonDates(seasons) {
  return seasons.map(season => ({
    ...season,
    start: parse(season.start.toString().split('T')[0], 'yyyy-MM-dd', new Date()),
    end: endOfDay(parse(season.end.toString().split('T')[0], 'yyyy-MM-dd', new Date()))
  }));
}

export function currencySign(symbol, withCurrencyName = true) {
  const relevantCurrencies = withCurrencyName ? currencies : currencySymbols;

  if (typeof symbol !== 'string') return relevantCurrencies.eur;
  return relevantCurrencies[symbol.toLowerCase()] ?? '';
}

export function getCurrency() {
  return localStorage.getItem('curr');
}

export function getCurrencySymbol() {
  return currencySign(getCurrency());
}

export function getParticipantsTotalCapacityEffect(participants) {
  return participants.reduce(
    (sum, participant) => participant.count * (participant.capacityEffect ?? 1) + sum,
    0
  );
}

export function strToAbsoluteDate(dateString) {
  const [y, m, d] = dateString.split('T')[0].split('-');
  return new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
}

export function formatTimeslot({ hours, minutes }) {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function isIntervalSeason(season) {
  if (!season) false;
  return season.tag === 'interval';
}

export function getPaymentStatus(bookingStatus, paymentDetails, bookingEditingDetails) {
  if (bookingStatus === 'refunded') {
    return PaymentStatuses.Refunded;
  }

  if (isNil(bookingEditingDetails)) {
    return paymentDetails?.tag === PaymentProviders.OnArrival &&
      paymentDetails?.status === 'unfulfilled'
      ? PaymentStatuses.Unpaid
      : PaymentStatuses.Paid;
  }

  const { stripe, onAccount, onArrival } = bookingEditingDetails;

  if ((stripe?.total?.amount || onAccount?.total?.amount) && !onArrival?.total?.amount) {
    return PaymentStatuses.Paid;
  } else if (!stripe?.total?.amount && !onAccount?.total?.amount && onArrival?.total?.amount) {
    return PaymentStatuses.Unpaid;
  }
  return PaymentStatuses.PartiallyPaid;
}

export function getTotalPaidWithoutFees(
  paymentDetails,
  bookingEditingDetails,
  fees,
  priceBreakdown
) {
  if (bookingEditingDetails) {
    return (
      bookingEditingDetails.stripe.total.amount +
      bookingEditingDetails.onAccount.total.amount -
      (bookingEditingDetails.onAccount.auxiliaryFee.amount +
        bookingEditingDetails.stripe.auxiliaryFee.amount)
    );
  }

  if (paymentDetails?.tag === PaymentProviders.Redeam || !paymentDetails?.tag) {
    return priceBreakdown.total;
  } else if (
    paymentDetails.tag === PaymentProviders.OnArrival &&
    paymentDetails.status === 'unfulfilled'
  ) {
    return 0;
  } else {
    const auxFeeAmount = fees?.some(f => f.type.includes('outside'))
      ? paymentDetails.applicationFee.amount
      : 0;
    return paymentDetails.payment.amount - auxFeeAmount;
  }
}

export function getTotalUnpaidWithoutFees(paymentDetails, bookingEditingDetails, fees) {
  if (!paymentDetails?.tag) {
    return 0;
  }
  if (bookingEditingDetails) {
    return (
      bookingEditingDetails.onArrival.total.amount -
      bookingEditingDetails.onArrival.auxiliaryFee.amount
    );
  }

  if (
    paymentDetails.tag === PaymentProviders.OnArrival &&
    paymentDetails.status === 'unfulfilled'
  ) {
    const auxFeeAmount = fees?.some(f => f.type.includes('outside'))
      ? paymentDetails.applicationFee.amount
      : 0;

    return paymentDetails.payment.amount - auxFeeAmount;
  } else {
    return 0;
  }
}

export function getTotalWithoutFeesFromEditingDetails(bookingEditingDetails) {
  if (!bookingEditingDetails) return;

  return bookingEditingDetails.total.total.amount - bookingEditingDetails.total.auxiliaryFee.amount;
}

export function getTotalFeesFromBreakdown(breakdown) {
  return breakdown.elements
    .filter(e => e.type == 'fee')
    .map(e => e.price)
    .reduce((sum, e) => (sum += e), 0);
}
export function noop() { }

export function constructFontDetails(fontUrl, fontId) {
  const fontFamily = `Company-custom-font-${fontId}`;
  const fontObject = {
    fontFamily,
    fontStyle: 'normal',
    fontDisplay: 'swap',
    fontWeight: 400,
    src: `url(${fontUrl})`
  };

  return [fontFamily, fontObject];
}

export function createReducer(reducers, initialState) {
  return function (state = initialState, action) {
    if (typeof reducers[action.type] === 'function')
      return reducers[action.type](state, action.payload);

    return state;
  };
}

const id = x => x;

export function createAction(type, payloadShaper = id) {
  return (...payload) => ({
    type,
    payload: payloadShaper(...payload)
  });
}

export function moneyFromDecimalToInteger({ amount, currency }) {
  const decimals = getCurrencyDecimalPoints(currency);
  const atomic = Math.pow(10, decimals);

  return Math.round(amount * atomic);
}

export function moneyFromIntegerToDecimal({ amount, currency }) {
  const isNegative = Math.sign(amount) < 0;
  const decimals = getCurrencyDecimalPoints(currency);
  const atomic = Math.pow(10, decimals);
  const integer = Math.floor(Math.abs(amount) / atomic);
  const floating = (Math.abs(amount) % atomic) / atomic;

  const res = integer + floating;
  return isNegative ? res * -1 : res;
}

export const isCurrencyZeroDecimal = currency => {
  return ZeroDecimalCurrencies.indexOf(currency) > -1;
};

export function formatMoney({ amount, currency }) {
  const isSelectedCurrencyZeroDecimal = isCurrencyZeroDecimal(currency);
  const decimals = getCurrencyDecimalPoints(currency);
  const atomic = Math.pow(10, decimals);

  if (isSelectedCurrencyZeroDecimal) {
    return `${Math.floor(amount / 100)}`;
  }
  return `${Math.floor(amount / atomic)}.${String(amount % atomic).padStart(decimals, 0)}`;
}

export function formatMoneyAndCurrency({ amount, currency }) {
  return `${currency.toUpperCase()} ${formatMoney({ amount, currency })}`;
}

export function formatMoneyAndCurrencySymbol({ amount, currency }, withCurrencyName = true) {
  return `${currencySign(currency, withCurrencyName)}${formatMoney({
    amount,
    currency
  })}`;
}

export function convertArrayToObject(array, key) {
  const initialValue = {};
  return array.reduce((obj, item) => {
    return {
      ...obj,
      [item[key]]: item
    };
  }, initialValue);
}

export function getFormattedOffsetTime(offset) {
  // offset from date is always in minutes and with opposite sign +-
  return offset * TIME_UNITS.minutes * -1;
}

export const TIME_UNITS = {
  seconds: 1000,
  minutes: 1000 * 60,
  hours: 1000 * 60 * 60,
  days: 1000 * 60 * 60 * 24
};

function checkForUndefined(value) {
  return !value ? 0 : value;
}

export function getCutoffTimeInMs(cutoffTimes) {
  if (!cutoffTimes) return 0;
  const daysInSec = checkForUndefined(cutoffTimes.days) * 60 * 60 * 24;
  const hoursInSec = checkForUndefined(cutoffTimes.hours) * 60 * 60;
  const minutesInSec = checkForUndefined(cutoffTimes.minutes) * 60;
  return (daysInSec + hoursInSec + minutesInSec) * 1000;
}

function addZeroIfNeeded(arg) {
  const str = String(arg).replace('-', '');
  if (str.length == 1) return `0${str}`;
  return str;
}

export function constructDateStringWithTimezone(date, timezoneName) {
  // returns string like this: 'YYYY-MM-DDTHH:MM:00.000Z'
  const offsetInMs = getTimezoneOffset(timezoneName, date);
  const symbol = String(offsetInMs)[0] == '-' ? '-' : '+';
  const hours = Math.floor(offsetInMs / (1000 * 60 * 60));
  const minutesWithSymbol = (offsetInMs % (1000 * 60 * 60)) / 60000;
  const minutes =
    String(minutesWithSymbol)[0] == '-' ? String(minutesWithSymbol).slice(1) : minutesWithSymbol;
  const offsetString = symbol + `${addZeroIfNeeded(hours)}:${addZeroIfNeeded(minutes)}`;
  const result = `${date.getFullYear()}-${addZeroIfNeeded(date.getMonth() + 1)}-${addZeroIfNeeded(
    date.getDate()
  )}T${addZeroIfNeeded(date.getHours())}:${addZeroIfNeeded(date.getMinutes())}:00.0${offsetString}`;
  return result;
}

export function eqBookingDate(x, y) {
  //if different types -> not equal
  if ('timeslot' in x !== 'timeslot' in y) {
    return false;
  }

  const datesEqual = new Date(x.date).getTime() === new Date(y.date).getTime();
  if (!datesEqual) {
    return false;
  }

  //if both has timeslots and timeslots are not equal -> not equal
  if (
    x.timeslot !== undefined &&
    y.timeslot !== undefined &&
    timeslotComparator(x.timeslot, y.timeslot) !== 0
  ) {
    return false;
  }

  return true;
}

export function timeslotComparator(a, b) {
  if (a.hours - b.hours !== 0) {
    return normalize(-1, 1, a.hours - b.hours);
  }

  return normalize(-1, 1, a.minutes - b.minutes);
}

function normalize(min, max, val) {
  if (val < min) return min;
  if (val > max) return max;

  return val;
}

export function isProductEnabled(product, companyProductDetails = {}) {
  if (product === SignupType.TicketingSystem) {
    // TS is disabled for the US Region
    return (
      process.env.REGION !== 'US' &&
      (!companyProductDetails || companyProductDetails.TicketingSystem)
    );
  }

  return !companyProductDetails || companyProductDetails.TipDirect;
}

// Card 288 — guest review replies and moderation. PROTOTYPE.
//
// THE KILL SWITCH. Flip this to false and the tab disappears from both nav
// lists and the route stops rendering, whatever any company flag says. It
// mirrors GUEST_REVIEWS_PROTOTYPE_ENABLED in the backend model, which unmounts
// the endpoints; the two are deliberately separate constants so a release can
// hide the UI without tearing the API out from under an in-flight request.
//
// This exists because the feature is unfinished prototype code sitting on
// `release`, and releases get cut off this branch before it is done.
export const GUEST_REVIEWS_PROTOTYPE_ENABLED = true;

// Strict opt-in per company, on top of the master switch. Stored at
// companyDetails.features.guestReviews — NOT a plan feature, because
// useIsFeatureForCompanyEnabled defaults unlisted features ON for paying plans.
export function isGuestReviewsEnabled(user) {
  return (
    GUEST_REVIEWS_PROTOTYPE_ENABLED &&
    user?.companyDetails?.features?.guestReviews?.enabled === true
  );
}

export function isPaymentsCompany(companyId, user = null) {
  const paymentsCompanies = [
    '60a6454e721adb56e3886271',
    '67c80b16b3b0dbdcf83d6bbb',
    '6641fa779dc970ab3ff8460c'
  ];
  if (paymentsCompanies.includes(companyId)) {
    return true;
  }
  if (user && user.profile && user.profile.payments && user.profile.payments.isPaymentsAccount === true) {
    return true;
  }
  return false;
}

// Card 119 — the first positive test for "this user is a guide".
//
// Everywhere else in the dashboard, guides are identified negatively: nav items
// carry `role: UserRoles.admin` and routes carry `adminOnly`, so "guide" has
// only ever meant "whatever is left". That works while guide-facing surfaces are
// the default, but it cannot express "show this to guides and no one else" —
// a payments analyst would fall through the same gap.
export function isTapToTipGuide(user) {
  return Boolean(user) && user.role === UserRoles.TapToTipAgent;
}

// Who sees the guide advice library.
//
// Guides are the audience. Admins at operator companies get it too: at small
// operators the owner also guides, and someone responsible for the account
// should be able to read what their guides are being told. Payments companies
// are excluded on merit rather than by policy — their guides never connect a
// bank, never receive tips and never collect reviews, so most of the library
// would be actively wrong for them.
//
// The dashboard nudge card is guide-only (see GuideAdviceCard): admins already
// have AgentSummary in that slot, and coaching aimed at guides reads as noise
// on an operator's dashboard.
export function canSeeGuideAdvice(user) {
  if (!user) return false;
  if (isPaymentsCompany(user.companyId, user)) return false;
  return isTapToTipGuide(user) || user.role === UserRoles.admin;
}

// Card 304 — who may open the operator Setup page.
//
// Setup carries commission settings, upsell and social config, branding and
// tours: operator business, not a guide's. `/tipdirect/setup` was always
// adminOnly, but its five sub-routes were not, so a guide who typed
// `/tipdirect/setup/tours` got the whole page. Nothing could be written —
// every backend handler demands system_admin — but it was operator data on a
// guide's screen, and every save button failed silently.
//
// Named here rather than inlined in the page so the route table and the page
// agree by construction, and so the rule is testable on its own.
//
// payments_analyst is included because PrivateRoute's own `adminOnly` check
// admits them; they are separately corralled to two paths before this is ever
// reached. Anything else — guides included — is out.
export function canSeeOperatorSetup(user) {
  if (!user) return false;
  return user.role === UserRoles.admin || user.role === UserRoles.paymentsAnalyst;
}

// Card 277/305 — is the tour photo feature switched on for this user's company?
//
// Strict `=== true`. Rollout is a manual per-company switch Andy sets by hand,
// so an absent, false or merely truthy value all read as off. Deliberately NOT
// routed through useIsFeatureForCompanyEnabled: that reads
// companyDetails.pricingSubscription (plan entitlements), and this is not a
// plan feature. Mirrors isTourPhotosEnabled in TA-Ticketing's backend model and
// util/tourPhotos.js in TipDirectApp.
export function isTourPhotosEnabledForCompany(user) {
  return user?.companyDetails?.features?.tourPhotos?.enabled === true;
}

// Card 323 — are guest questionnaires switched on for this user's company?
//
// Same contract as tour photos: a strict opt-in on companyDetails.features,
// toggled by support in the Multitool, never a plan entitlement. Mirrors
// isQuestionnairesEnabled in TA-Ticketing's backend questionnaire model.
export function isQuestionnairesEnabledForCompany(user) {
  return user?.companyDetails?.features?.questionnaires?.enabled === true;
}

// Card 362 — does this user hold their own TipDirect (Guide) Pro subscription?
//
// The Stripe webhook (backend WebhookController) writes `status: 'active'` on
// checkout and `'cancelled'` on cancellation, nothing else. SettingsPage shows
// the "Subscriptions" panel, with Manage plan -> Stripe billing portal, on this
// test; nowhere else in the app offers that portal.
export function hasActiveOwnSubscription(user) {
  return user?.profile?.subscription?.status === 'active';
}

// Card 362 — who the Settings tab is for, on the desktop rail and the mobile
// menu alike. Admins get the company settings. Everyone else (guides) gets
// only their own Pro subscription panel there, so for a guide without one the
// page is empty and the tab would lead nowhere. Payments analysts never: their
// routes are corralled to Payments and Stripe.
export function canSeeSettingsTab(user) {
  if (!user || user.role === UserRoles.paymentsAnalyst) return false;
  return user.role === UserRoles.admin || hasActiveOwnSubscription(user);
}

// Card 349 — who sees the Product Improvement (PIP) tab.
//
// Decided by Andy, 2026-10-07: company admins whose company holds
// `productCredits > 0`, plus TripAdmit's own staff. Guides and payments
// analysts never. One predicate for the desktop rail, the mobile menu and the
// page itself, so the three cannot disagree (the mobile menu once missed PIP
// entirely, card 296).
//
// There is no separate TripAdmit-staff role: in TipDirect the company admin IS
// `system_admin`. Staff means a system_admin on a tripadmit.com address, the
// domain PIP's own magic-link login already treats as staff.
//
// This only hides the tab. The gate a typed URL cannot pass is PIP's
// `POST /auth/sso`, which applies the same rule — MIRRORS
// pip/backend/src/services/pipAccess.js `pipAccess`.
export const PIP_STAFF_EMAIL_DOMAIN = 'tripadmit.com';

export function isTripAdmitStaff(user) {
  if (!user || user.role !== UserRoles.admin) return false;
  const email = typeof user.email === 'string' ? user.email.trim().toLowerCase() : '';
  return email.endsWith(`@${PIP_STAFF_EMAIL_DOMAIN}`);
}

export function companyProductCredits(user) {
  const raw = user?.companyDetails?.productCredits;
  return Number.isFinite(raw) ? raw : 0;
}

export function canSeeProductImprovement(user) {
  if (!user || user.role !== UserRoles.admin) return false;
  return isTripAdmitStaff(user) || companyProductCredits(user) > 0;
}

// Card 237 — which of the two Payments views a user can reach.
//
// The Payments page has always had two mutually exclusive bodies: Stripe's
// embedded per-account component, and our own transaction table. Before this
// card the choice was implicit — the embedded view simply won whenever the user
// had a usable Stripe account — which meant an admin who claimed a tip onto
// their own account silently lost the company-wide list they had been using.
// Naming both halves lets the page offer them as a choice instead.
export const PaymentsView = {
  OWN_STRIPE: 'ownStripe',
  COMPANY_TRANSACTIONS: 'companyTransactions'
};

// Whether Stripe's embedded component has an account to render. Payments
// companies are excluded because their guides are paid through the platform
// account, not a Connect account of their own.
export function hasOwnStripePaymentsView(user) {
  if (!user) return false;
  if (isPaymentsCompany(user.companyId, user)) return false;

  const capabilities = user.profile?.stripeConnectCustom?.capabilities;
  if (!user.profile?.stripeConnectCustom) return false;

  // Cross-border guides charge on the platform account, so card_payments is
  // never active on their own account and must not be required here.
  return userIsCrossBorder(user.profile?.country)
    ? capabilities?.transfers === 'active'
    : capabilities?.card_payments === 'active' && capabilities?.transfers === 'active';
}

// Whether the company-wide transaction list is available to this user.
//
// This mirrors the server's own rule in the backend's `GetAllTransactionsAdaptor`:
// GET /tipdirect/transactions with no `userId` returns the whole company for a
// system_admin unless the company has disableCompanyTransactionReporting set,
// and returns only the caller's own rows for everyone else. The server is
// authoritative — this predicate exists so the UI does not offer a view the API
// would answer with someone else's data.
export function canSeeCompanyTransactions(user) {
  if (!user) return false;
  if (user.role !== UserRoles.admin) return false;
  return !user.companyDetails?.features?.disableCompanyTransactionReporting;
}

// The toggle is only worth showing when both views actually exist. An admin
// without a Stripe account has only the table, and a guide with one has only
// the embedded view; neither should see a switch with a dead half.
export function canSwitchPaymentsView(user) {
  return hasOwnStripePaymentsView(user) && canSeeCompanyTransactions(user);
}

// Script to Programmatically Lighten or Darken a hex color
// https://stackoverflow.com/a/13542669/16580563
export const changeColorBrightness = (p, c0, c1, l) => {
  let r,
    g,
    b,
    P,
    f,
    t,
    h,
    i = parseInt,
    m = Math.round,
    a = typeof c1 == 'string';
  if (
    typeof p != 'number' ||
    p < -1 ||
    p > 1 ||
    typeof c0 != 'string' ||
    (c0[0] != 'r' && c0[0] != '#') ||
    (c1 && !a)
  )
    return null;
  const globalObj = {};
  if (!globalObj.pSBCr)
    globalObj.pSBCr = d => {
      let n = d.length,
        x = {};
      if (n > 9) {
        ([r, g, b, a] = d = d.split(',')), (n = d.length);
        if (n < 3 || n > 4) return null;
        (x.r = i(r[3] == 'a' ? r.slice(5) : r.slice(4))),
          (x.g = i(g)),
          (x.b = i(b)),
          (x.a = a ? parseFloat(a) : -1);
      } else {
        if (n == 8 || n == 6 || n < 4) return null;
        if (n < 6) d = '#' + d[1] + d[1] + d[2] + d[2] + d[3] + d[3] + (n > 4 ? d[4] + d[4] : '');
        d = i(d.slice(1), 16);
        if (n == 9 || n == 5)
          (x.r = (d >> 24) & 255),
            (x.g = (d >> 16) & 255),
            (x.b = (d >> 8) & 255),
            (x.a = m((d & 255) / 0.255) / 1000);
        else (x.r = d >> 16), (x.g = (d >> 8) & 255), (x.b = d & 255), (x.a = -1);
      }
      return x;
    };
  (h = c0.length > 9),
    (h = a ? (c1.length > 9 ? true : c1 == 'c' ? !h : false) : h),
    (f = globalObj.pSBCr(c0)),
    (P = p < 0),
    (t =
      c1 && c1 != 'c'
        ? globalObj.pSBCr(c1)
        : P
          ? { r: 0, g: 0, b: 0, a: -1 }
          : { r: 255, g: 255, b: 255, a: -1 }),
    (p = P ? p * -1 : p),
    (P = 1 - p);
  if (!f || !t) return null;
  if (l) (r = m(P * f.r + p * t.r)), (g = m(P * f.g + p * t.g)), (b = m(P * f.b + p * t.b));
  else
    (r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)),
      (g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)),
      (b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5));
  (a = f.a),
    (t = t.a),
    (f = a >= 0 || t >= 0),
    (a = f ? (a < 0 ? t : t < 0 ? a : a * P + t * p) : 0);
  if (h)
    return (
      'rgb' + (f ? 'a(' : '(') + r + ',' + g + ',' + b + (f ? ',' + m(a * 1000) / 1000 : '') + ')'
    );
  else
    return (
      '#' +
      (4294967296 + r * 16777216 + g * 65536 + b * 256 + (f ? m(a * 255) : 0))
        .toString(16)
        .slice(1, f ? undefined : -2)
    );
};

export const getSortedGuidesForTeam = (users, currentUserId) => {
  return users.sort((a, b) => {
    // 1. Move the current user to the top
    if (a._id === currentUserId) return -1;
    if (b._id === currentUserId) return 1;

    // 2. Sort users with avatarUrl first, then alphabetically by firstName and lastName
    if (a.profile.avatarUrl && b.profile.avatarUrl) {
      return (
        a.profile?.firstName.localeCompare(b.profile?.firstName) ||
        a.profile?.lastName.localeCompare(b.profile?.lastName)
      );
    }
    if (a.profile.avatarUrl) return -1;
    if (b.profile.avatarUrl) return 1;

    // 3. Sort alphabetically users without avatarUrl
    return (
      a.profile?.firstName.localeCompare(b.profile?.firstName) ||
      a.profile?.lastName.localeCompare(b.profile?.lastName)
    );
  });
};
